import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { AuthContext } from "utils/AuthContext";
import { FlexLeftCol, FlexSpaceBetween } from "styles/global-style";
import Input from "components/Input";
import Button from "components/Button";
import { ImageCropper } from "components/ImageCropper";
import { useNavigate } from "react-router-dom";

export default function Organization() {
  const { supabase, user, refresh, roleAccess } = useContext(AuthContext);
  const navigate = useNavigate();

  const [org, setOrg] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const nameRef = useRef();

  //----------------------------------------------------

  const fetchData = useCallback(async () => {
    if (!user?.org?.id) return;
    
    setIsLoading(true);
    const { data } = await supabase.from("orgs").select().eq("id", user.org.id);
    if (data && data[0]) {
      setOrg(data[0]);
    }
    
    // Fetch organization logo
    const logoImage = await supabase.from("images").select("url").eq("org_id", user.org.id).eq("type", "logo");
    if (logoImage.data?.[0]) {
      const signedUrl = await supabase.storage.from("logos").createSignedUrl(logoImage.data[0].url, 3600);
      setOrgLogo(signedUrl?.data?.signedUrl);
    } else {
      setOrgLogo(null); // Clear logo state when no logo found
    }
    setIsLoading(false);
  }, [supabase, user]);

  const onLogoSubmit = useCallback(
    (selectedFile, croppedFile, current) => {
      try {
        // First remove previous logo, if any
        if (current) {
          supabase.storage.from("logos").remove(current.split("/").at(-1));
        }

        // Now upload the new one
        const fileExt = selectedFile.name.split(".").pop();
        const fileName = `${Math.random().toString(16).slice(2)}.${fileExt}`;
        const filePath = `${fileName}`;

        supabase.storage
          .from("logos")
          .upload(filePath, croppedFile)
          .then(async (res) => {
            if (!res.error) {
              // Add it's info to images table
              const result = current
                ? await supabase.from("images").update({ url: fileName }).eq("org_id", user.org.id).eq("type", "logo")
                : await supabase
                    .from("images")
                    .insert({
                      type: "logo",
                      url: fileName,
                      org_id: user.org.id,
                    })
                    .select("id");

              // Update the org with image_id reference
              if (!current) {
                await supabase.from("orgs").update({ image_id: result.data[0].id }).eq("id", user.org.id);
              }
              
              // Refresh data to show new logo
              fetchData();
              refresh(); // Update user context for main menu
            }
          });
      } catch (error) {
        console.error(error);
      }
    },
    [supabase, user.org.id, fetchData, refresh]
  );

  const removeLogo = useCallback(async () => {
    try {
      // Remove logo from storage
      if (orgLogo) {
        const logoImage = await supabase.from("images").select("url").eq("org_id", user.org.id).eq("type", "logo");
        if (logoImage.data?.[0]) {
          await supabase.storage.from("logos").remove(logoImage.data[0].url);
        }
      }

      // Remove from images table
      await supabase.from("images").delete().eq("org_id", user.org.id).eq("type", "logo");

      // Update org to remove image_id reference
      await supabase.from("orgs").update({ image_id: null }).eq("id", user.org.id);

      // Refresh data
      fetchData();
      refresh(); // Update user context for main menu
    } catch (error) {
      console.error(error);
    }
  }, [supabase, user.org.id, orgLogo, fetchData, refresh]);

  const submit = useCallback(async () => {
    const input = nameRef.current.value.trim();
    if (input) {
      const newName = input ? input : user.firstname + "'s org";
      await supabase.from("orgs").update({ name: newName }).eq("id", user.current_org_id);
    }
    fetchData();
    refresh();
  }, [supabase, fetchData, user.current_org_id, user.firstname, refresh]);

  const deleteOrg = useCallback(async () => {
    if (
      window.confirm(
        "DANGER!\n\nYou are about to delete your whole organization, and all associated projects and data. There is no going back after this.\n\nARE YOU ABSOLUTLEY CERTAIN?"
      )
    ) {
      if (window.confirm("Seriously? Just making sure cause I care..\n\nShould I delete your organization?")) {
        // Delete org's uploaded images from Storage
        const orgImgs = await supabase.from("images").select("url").eq("org_id", user.org.id);

        if (orgImgs.data?.length > 0) {
          const arr = orgImgs.data.map((val) => val.url.split("/").at(-1));
          supabase.storage.from("logos").remove(arr);
        }

        // Start deleting table rows, order important due to db fkeys
        await supabase.from("orgs").delete().eq("id", user.org.id);

        refresh();
        navigate("/auth/orgproj");
      }
    }
  }, [navigate, refresh, supabase, user]);

  //----------------------------------------------------

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (org && org.name && nameRef.current && !isLoading) {
      nameRef.current.value = org.name;
    }
  }, [org, isLoading]);

  return (
    <FlexLeftCol>
      <h2>Organization</h2>
      <FlexSpaceBetween>
        <div style={{ flexGrow: 1 }}>
          <Input caption="Name" ref={nameRef} onChangeDelay={submit} white left />
        </div>
        <div style={{ marginLeft: 20 }}>
          <ImageCropper 
            thumb={orgLogo} 
            onFileSubmit={onLogoSubmit} 
            hideCardPreview 
            aspectRatio={2.5}
            rectangular={true}
            caption="Logo"
            onRemove={removeLogo}
          />
        </div>
      </FlexSpaceBetween>
      
      {roleAccess("admin") && (
        <>
          <p style={{ color: "red", marginTop: 80 }}>
            <b>Dangerzone</b>
          </p>
          <Button text="Delete organization" callback={deleteOrg} color="red" />
        </>
      )}
    </FlexLeftCol>
  );
}
